import React, { useState } from 'react';
import { Card, Upload, Button, message, Spin, Space, Typography, Row, Col } from 'antd';
import { InboxOutlined, FilePdfOutlined, FileExcelOutlined, DownloadOutlined, ReloadOutlined, RobotOutlined, FileImageOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';

const { Dragger } = Upload;
const { Title, Paragraph } = Typography;

const Pdf2ExcelPage = () => {
    const { darkMode } = useTheme();
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [converted, setConverted] = useState(false);

    const isPdfFile = (file) => {
        return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    };

    const isImageFile = (file) => {
        const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
        return imageTypes.includes(file.type) || 
               imageExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    };

    const getFileIcon = (file) => {
        if (isPdfFile(file)) {
            return <FilePdfOutlined style={{ marginRight: 8, color: '#ff4d4f' }} />;
        } else if (isImageFile(file)) {
            return <FileImageOutlined style={{ marginRight: 8, color: '#52c41a' }} />;
        }
        return <InboxOutlined style={{ marginRight: 8 }} />;
    };

    const handleConvert = async () => {
        if (!file) {
            message.error('Lütfen bir PDF veya image dosyası yükleyin.');
            return;
        }

        setLoading(true);
        setConverted(false);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pdf2excel/convert`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    responseType: 'blob', 
                }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `fatura_${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setConverted(true);
            message.success('Dosya başarıyla Excel formatına dönüştürüldü');
        } catch (error) {
            console.error('Conversion error:', error);
            let errorMessage = 'Dönüştürme sırasında bir hata oluştu.';
            
            if (error.response) {
                if (error.response.status === 400) {
                    errorMessage = error.response.data?.detail || 'Geçersiz dosya formatı.';
                } else if (error.response.status === 500) {
                    errorMessage = error.response.data?.detail || 'Sunucu hatası oluştu.';
                }
            }
            
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const uploadProps = {
        accept: '.pdf,.jpg,.jpeg,.png,.gif,.webp',
        maxCount: 1,
        onRemove: () => {
            setFile(null);
            setConverted(false);
        },
        beforeUpload: (file) => {
            const isValidPdf = isPdfFile(file);
            const isValidImage = isImageFile(file);
            
            if (!isValidPdf && !isValidImage) {
                message.error(`${file.name} geçerli bir PDF veya image dosyası değil!`);
                return Upload.LIST_IGNORE;
            }

            if (file.size > 10 * 1024 * 1024) {
                message.error("Dosya boyutu 10MB'dan küçük olmalıdır!");
                return Upload.LIST_IGNORE;
            }

            setFile(file);
            setConverted(false);
            return false; 
        },
        fileList: file ? [file] : [],
    };

    const handleReset = () => {
        setFile(null);
        setConverted(false);
        message.info('Form sıfırlandı.');
    };

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}>
                <RobotOutlined style={{ marginRight: 10 }} />
                E-Fatura Dönüştürücü
            </Title>
            <Paragraph>
                PDF veya image formatındaki e-faturaları excel formatına dönüştürün. 
            </Paragraph>
            <Row gutter={[24, 24]}>
                <Col xs={24} lg={24}>
                    <Card 
                        title="Fatura Dosyası Yükleme" 
                        bordered={false} 
                        style={{ height: '100%' }}
                        extra={
                            file && (
                                <Space>
                                    <Button 
                                        icon={<ReloadOutlined />} 
                                        onClick={handleReset}
                                        size="small"
                                    >
                                        Sıfırla
                                    </Button>
                                </Space>
                            )
                        }
                    >
                        <Dragger 
                            {...uploadProps} 
                            style={{ 
                                padding: 20, 
                                background: darkMode ? '#1f1f1f' : '#fafafa',
                                border: darkMode ? '1px dashed #434343' : '1px dashed #d9d9d9'
                            }}
                        >
                            <p className="ant-upload-drag-icon" style={{ color: darkMode ? '#fff' : '#000' }}>
                                <InboxOutlined style={{ fontSize: 48 }} />
                            </p>
                            <p className="ant-upload-text" style={{ color: darkMode ? '#fff' : '#000' }}>
                                Tıklayın veya PDF/image dosyasını bu alana sürükleyin
                            </p>
                        </Dragger>

                        {file && (
                            <div style={{ marginTop: 16 }}>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <div style={{ 
                                        padding: 12, 
                                        background: darkMode ? '#141414' : '#f0f0f0',
                                        borderRadius: 4,
                                        border: darkMode ? '1px solid #303030' : '1px solid #d9d9d9'
                                    }}>
                                        {getFileIcon(file)}
                                        <span style={{ color: darkMode ? '#fff' : '#000' }}>
                                            {file.name}
                                        </span>
                                        <span style={{ 
                                            marginLeft: 8, 
                                            color: darkMode ? '#999' : '#666',
                                            fontSize: '12px'
                                        }}>
                                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                        </span>
                                    </div>
                                </Space>
                            </div>
                        )}

                        <Button 
                            type="primary" 
                            icon={<FileExcelOutlined />} 
                            size="large" 
                            block 
                            style={{ marginTop: 24 }}
                            onClick={handleConvert}
                            loading={loading}
                            disabled={!file || loading}
                        >
                            {loading ? 'Dönüştürülüyor...' : "Excel'e Dönüştür"}
                        </Button>

                        {loading && (
                            <div style={{ 
                                marginTop: 16, 
                                textAlign: 'center',
                                padding: 16,
                                background: darkMode ? '#141414' : '#f0f0f0',
                                borderRadius: 4
                            }}>
                                <Spin size="large" tip="Dosya Excel formatına dönüştürülüyor..." />
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Pdf2ExcelPage;